/*
 * ---------------------------------------------------------------------------------------------------------------------
 * GLOBAL NAV – desktop flyout menu, behaviour measured on apple.com
 * ---------------------------------------------------------------------------------------------------------------------
 *
 *  hover intent   opens 120ms after the pointer lands on an item
 *  switching      170ms on another item swaps the content in place: 120ms cross-fade + 120ms height morph,
 *                 then the new flyout is "open" (≈200ms later). The panel never closes in between.
 *  closing        as soon as the pointer leaves the header + panel: items fade out, the panel collapses after
 *                 120ms, the curtain fades; the header returns to its blended look when the panel is gone (~400ms).
 *  panel rate     max(240ms, height × 0.46ms) – taller panels open a touch slower, exactly like apple.com
 *  touch / pen    first tap opens, second tap follows the link
 *  keyboard       the hidden chevron after each item (shown on focus) toggles; Esc closes and returns focus
 *
 * Everything visual is CSS (assets/globalnav.css); this file only switches classes and sets the panel height.
 */
(() => {
  if (window.customElements.get('global-nav')) return;

  const EASE = 'cubic-bezier(0.4, 0, 0.6, 1)';
  const SWITCH_MS = 120;          // cross-fade + height morph when changing flyout
  const SWITCH_SETTLE_MS = 200;   // after this the new flyout becomes the "open" one
  const CLOSE_HEIGHT_DELAY = 120; // panel waits for the items to start fading
  const CLOSE_TOTAL_MS = 420;     // when the header can go back to its blended look

  class GlobalNav extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('x-header') || this.parentElement;
      this.bg = this.querySelector('.globalnav-flyouts__bg');
      this.flyouts = Array.from(this.querySelectorAll('[data-flyout]'));
      this.triggers = Array.from(this.header.querySelectorAll('[data-globalnav-trigger]'));
      this.toggles = Array.from(this.header.querySelectorAll('[data-globalnav-toggle]'));
      this.closers = Array.from(this.header.querySelectorAll('[data-globalnav-plain], .header__secondary-nav, .header__logo'));
      this.curtain = document.getElementById('globalnav-curtain');

      this.activeId = null;
      this.pendingId = null;
      this.timers = {};
      this.abort = new AbortController();
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.abort.signal });

      this.triggers.forEach((trigger) => {
        const id = trigger.getAttribute('data-globalnav-trigger');
        trigger.setAttribute('aria-expanded', 'false');
        on(trigger, 'pointerenter', (e) => this.onTriggerEnter(e, id));
        on(trigger, 'pointerleave', (e) => this.onTriggerLeave(e, id));
        on(trigger, 'click', (e) => this.onTriggerClick(e, id));
      });

      this.toggles.forEach((toggle) => {
        const id = toggle.getAttribute('data-globalnav-toggle');
        on(toggle, 'click', (e) => {
          e.preventDefault();
          if (this.activeId === id) {
            this.close();
          } else {
            this.open(id);
            this.focusFirstLink(id);
          }
        });
      });

      // Pointer over a plain link, the logo or the icons while a flyout is open -> close (like apple.com)
      this.closers.forEach((el) => on(el, 'pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !this.activeId) return;
        this.cancel('open');
        this.later('close', () => this.close(), 170);
      }));

      on(this.header, 'pointerenter', (e) => { if (e.pointerType === 'mouse') this.cancel('close'); });
      on(this.header, 'pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.cancel('open');
        this.pendingId = null;
        if (this.activeId) this.close();
      });
      on(this, 'pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.cancel('open');
        this.cancel('close');
        this.pendingId = null;
      });

      on(document, 'keydown', (e) => {
        if (e.key !== 'Escape' || !this.activeId) return;
        const id = this.activeId;
        this.close();
        this.header.querySelector(`[data-globalnav-toggle="${id}"]`)?.focus();
      });
      on(this.header, 'focusout', (e) => {
        if (this.activeId && e.relatedTarget && !this.header.contains(e.relatedTarget)) this.close();
      });
      on(document, 'click', (e) => {
        if (this.activeId && !this.header.contains(e.target)) this.close();
      });

      // Search, cart and mobile menu must never open on top of a flyout
      this.header.querySelectorAll('[aria-controls^="header-search"], [aria-controls="cart-drawer"], [aria-controls="sidebar-menu"]')
        .forEach((el) => on(el, 'click', () => this.activeId && this.close({ immediate: true })));

      on(window, 'resize', () => this.activeId && this.setHeight(this.heightOf(this.activeId), 0));
      const mq = window.matchMedia('(min-width: 1000px)');
      on(mq, 'change', (e) => { if (!e.matches) this.close({ immediate: true }); });

      if (window.Shopify && Shopify.designMode) {
        this.flyouts.forEach((flyout) => {
          on(flyout, 'shopify:block:select', () => this.open(flyout.id));
          on(flyout, 'shopify:block:deselect', () => this.close());
        });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      Object.keys(this.timers).forEach((k) => this.cancel(k));
      document.documentElement.classList.remove('globalnav-open');
      this.curtain?.classList.remove('show-curtain');
    }

    get openDelay() { return parseInt(this.getAttribute('open-delay') || '120', 10); }
    get switchDelay() { return parseInt(this.getAttribute('switch-delay') || '170', 10); }

    isDesktop() { return window.matchMedia('(min-width: 1000px)').matches; }
    later(name, fn, ms) { this.cancel(name); this.timers[name] = setTimeout(fn, ms); }
    cancel(name) { clearTimeout(this.timers[name]); delete this.timers[name]; }
    flyout(id) { return this.flyouts.find((f) => f.id === id); }

    /* ---------------------------------------------------------------------------------------------------- pointer */

    onTriggerEnter(event, id) {
      if (event.pointerType !== 'mouse' || !this.isDesktop()) return;
      this.cancel('close');
      if (this.activeId === id) {
        this.cancel('open');
        this.pendingId = null;
        return;
      }
      this.pendingId = id;
      this.later('open', () => { if (this.pendingId === id) this.open(id); }, this.activeId ? this.switchDelay : this.openDelay);
    }

    onTriggerLeave(event, id) {
      if (event.pointerType === 'mouse' && this.pendingId === id) {
        this.cancel('open');
        this.pendingId = null;
      }
    }

    onTriggerClick(event, id) {
      if (!this.isDesktop()) return;
      const isTouch = event.pointerType ? event.pointerType !== 'mouse' : window.matchMedia('(hover: none)').matches;
      if (isTouch && this.activeId !== id) {
        event.preventDefault();
        this.open(id);
      }
    }

    /* ---------------------------------------------------------------------------------------------------- states */

    open(id) {
      const next = this.flyout(id);
      if (!next || id === this.activeId) return;
      ['open', 'close', 'settle', 'cleanup'].forEach((t) => this.cancel(t));
      this.pendingId = null;

      const previous = this.flyout(this.activeId);
      this.activeId = id;
      this.setExpanded(id);
      document.documentElement.classList.add('globalnav-open');
      this.classList.add('is-open');
      this.curtain?.classList.add('show-curtain');

      // Anything still fading from an earlier switch / close is dropped right away
      this.flyouts.forEach((f) => {
        if (f !== next && f !== previous) f.classList.remove('is-active', 'is-change-next', 'is-change-prev', 'is-closing');
      });

      const height = this.heightOf(id);

      if (previous) {
        // SWITCH: cross-fade the content and morph the panel, no re-stagger
        previous.classList.remove('is-active', 'is-change-next', 'is-closing');
        previous.classList.add('is-change-prev');
        next.classList.remove('is-active', 'is-closing', 'is-change-prev');
        next.classList.add('is-change-next');
        this.setHeight(height, SWITCH_MS);

        this.later('settle', () => {
          // drop the old content instantly (it is already faded out), so nothing can flash back
          previous.classList.add('is-reset');
          previous.classList.remove('is-change-prev');
          void previous.offsetHeight;
          previous.classList.remove('is-reset');
          next.classList.remove('is-change-next');
          next.classList.add('is-active');
        }, SWITCH_SETTLE_MS);
      } else {
        // OPEN from closed (or from a panel that was still collapsing)
        next.classList.remove('is-closing', 'is-change-next', 'is-change-prev');
        void next.offsetHeight; // restart the entrance stagger
        next.classList.add('is-active');
        this.setHeight(height, this.rateFor(height));
      }
    }

    close({ immediate = false } = {}) {
      ['open', 'close', 'settle'].forEach((t) => this.cancel(t));
      this.pendingId = null;
      if (!this.activeId) return;

      const current = this.flyout(this.activeId);
      const height = this.heightOf(this.activeId);
      this.activeId = null;
      this.setExpanded(null);
      this.classList.remove('is-open');
      this.curtain?.classList.remove('show-curtain');

      this.flyouts.forEach((f) => f.classList.remove('is-active', 'is-change-next', 'is-change-prev'));

      if (immediate) {
        this.flyouts.forEach((f) => f.classList.remove('is-closing'));
        this.setHeight(0, 0);
        document.documentElement.classList.remove('globalnav-open');
        return;
      }

      current?.classList.add('is-closing');
      this.setHeight(0, this.rateFor(height), CLOSE_HEIGHT_DELAY);

      this.later('cleanup', () => {
        current?.classList.remove('is-closing');
        document.documentElement.classList.remove('globalnav-open');
      }, Math.max(CLOSE_TOTAL_MS, this.rateFor(height) + CLOSE_HEIGHT_DELAY + 40));
    }

    /* ---------------------------------------------------------------------------------------------------- helpers */

    heightOf(id) {
      const flyout = this.flyout(id);
      return flyout ? Math.ceil(flyout.getBoundingClientRect().height) : 0;
    }

    rateFor(height) {
      return Math.round(Math.max(240, height * 0.46));
    }

    setHeight(px, durationMs, delayMs = 0) {
      this.bg.style.transition = durationMs ? `height ${durationMs}ms ${EASE} ${delayMs}ms` : 'none';
      this.bg.style.height = `${px}px`;
    }

    setExpanded(id) {
      this.triggers.forEach((t) => {
        const isOpen = t.getAttribute('data-globalnav-trigger') === id;
        t.setAttribute('aria-expanded', String(isOpen));
        t.closest('.globalnav__item')?.classList.toggle('is-current-flyout', isOpen);
      });
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', String(t.getAttribute('data-globalnav-toggle') === id)));
    }

    focusFirstLink(id) {
      setTimeout(() => this.flyout(id)?.querySelector('a')?.focus({ preventScroll: true }), 60);
    }
  }

  window.customElements.define('global-nav', GlobalNav);
})();
